<template>
    <div v-if="visible">
        <div class="container">
            <EmployeeFilter class="row"
                :fields="['area', 'project', 'username']"
                @params="params => queryParams = params"
                @filteredUsers="users => refresh(users.map(user => user.login))"/>
            <div class="row">
                <div class="col-md-12 m-b-30 label-style top-header-row">
                    <div class="text-left">
                        Bieżący tydzień jest <b>{{ currentWeekParity === 0 ? 'parzysty' : 'nieparzysty' }}</b> - (
                        <span class="adjust-plan" @click="adjustPlan = !adjustPlan">
                            <span v-if="adjustPlan"> Wyświetl cały plan</span>
                            <span v-else> Wyświetl plan tylko na ten tydzień</span>
                        </span> )
                    </div>
                    <div class="color-legend">
                        <div v-for="place in places"
                            :style="'background-color:'+getPlaceColor(place.id)">
                            {{ place.name }}
                        </div>
                    </div>
                </div>
            </div>
            <transition name="fade" mode="out-in">
            <div class="box-bordered p-l-10 p-r-10">
                <div class="table-responsive">
                    <table class="table text-align-c table-pp">
                        <thead>
                        <tr>
                            <th>pracownik</th>
                            <th v-for="(weekday, weekdayId) in weekdays"
                                :class="{'highlight-today': currentDow === weekdayId}">{{ weekday }}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(plan, emp) in planGrouped" :key="emp">
                            <td class="right-border-column">
                                <div :class="{'half-time-hours-warning': !halfTimeHoursMet(plan)}">
                                    {{ emp }}<span v-if="!halfTimeHoursMet(plan)">*</span>
                                </div>
                                <div v-if="numberOfPlacesChosen(plan) > 0" class="container">
                                    <hr>
                                    <div class="week-hours-places" v-for="(time, place) in weekHours(plan)"
                                         :style="{'background-color': getPlaceColor(place), padding: '3px'}">
                                        {{ weekMinutesFormatted(time.current) }}
                                        <span v-if="time.current !== time.next && time.next !== 0">
                                                    | {{ weekMinutesFormatted(time.next) }}</span>
                                    </div>
                                    <strong v-if="numberOfPlacesChosen(plan) !== 1">{{ hoursSumWeekParity(plan)
                                        }}</strong>
                                </div>
                            </td>
                            <td v-for="(dowGroup, dowId) in plan">
                                <WorktimeEditableHour v-for="dowPlan in dowGroup"
                                                      v-if="!adjustPlan || matchingCurrentWeekId(dowPlan.week_id)"
                                                      :plan="dowPlan"
                                                      :places="places"
                                                      :editable="editable(emp)"
                                                      :color="getPlaceColor(dowPlan.place)"
                                                      @updated="updateUpdated"
                                                      @deleted="updateDeleted">
                                </WorktimeEditableHour>
                                <div v-if="editable(emp)">
                                    <div class="btn btn-link" data-toggle="modal"
                                         :data-target="'#'+getAddModalId(dowId)">
                                        +
                                    </div>
                                    <WorktimeFormModal
                                            :modalId="getAddModalId(dowId)"
                                            :dow="dowId+1"
                                            :fromTimeValueDefault="'09:00'"
                                            :toTimeValueDefault="'17:00'"
                                            :places="places"
                                            @created="updateCreated">
                                    </WorktimeFormModal>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
        </div>
        </transition>
         <div v-show="!allHalfTimeHoursMet">
                <div class="half-time-hours-warning">
                    * Plan pracy obejmuje mniej niż 20 godzin!
                </div>
         </div>
        </div>
    </div>
</template>

<script>
    import {backend} from '../../backend';
    import Constants from '../../constants.js';

    import EmployeeFilter from "./EmployeeFilter.vue";
    import WorktimeEditableHour from './WorktimeEditableHour.vue';
    import WorktimeFormModal from './WorktimeFormModal.vue';

    moment.locale('pl');

    export default {
        name: "WorktimePlan",
        components: {
            EmployeeFilter,
            WorktimeEditableHour,
            WorktimeFormModal
        },
        data() {
            return {
                employeeName: '',
                loggedUsername: '',
                planGrouped: null,
                queryParams: {},
                places: null,
                weekdays: moment.weekdays(true),
                placeColor: Constants.get('IA_PLACE_COLOR'),
                weekParityDict: Constants.get('IA_WORKPLAN_WEEK_PARITY'),
                currentWeekParity: moment().week() % 2,
                adjustPlan: false,
                visible: false,
                loaded: false,
                currentDow: moment().weekday(),
                allHalfTimeHoursMet: true,
            }
        },
        created() {
            this.loggedUsername = this.$store.getters['auth/username'];
            this.employeeName = this.loggedUsername;
            this.allHalfTimeHoursMet = true;
            this.$store.dispatch('ia/places')
                .then(data => {
                    this.places = data;
                })
                .catch(() => {
                    this.$vs.notify({text: 'Nie udało się pobrać listy miejsc.', color: 'danger'});
                });
            this.queryParams = {username: this.loggedUsername};
            this.refresh([this.loggedUsername]);
        },
        methods: {
            numberOfPlacesChosen: function (plan) {
                return Object.keys(this.weekHours(plan)).length;
            },
            getAddModalId: dow => 'addModal' + dow,
            editable: function (emp) {
                return this.loggedUsername === emp;
            },
            getPlaceColor: function (place) {
                return this.placeColor[place];
            },
            matchingCurrentWeekId: function (weekId) {
                const mod2 = this.weekParityDict[weekId].mod2;
                return this.currentWeekParity === mod2 || !(mod2 in [0, 1]);
            },
            matchingNextWeekId: function (weekId) {
                const mod2 = this.weekParityDict[weekId].mod2;
                return this.currentWeekParity !== mod2 || !(mod2 in [0, 1]);
            },
            refresh: function (usernames) {
                this.loaded = false;
                backend.getWorktimePlan(this.queryParams)
                    .then((response) => {
                        let plan = {};
                        usernames.forEach(username => plan = this.createPlanSlot(plan, username));
                        response.data.filter(el => el.dow <= 7).forEach(el => plan[el.user][el.dow - 1].push(el));
                        for (var emp in plan) {
                            plan[emp].forEach(sublist => sublist.sort(this.planSort));
                        }
                        this.allHalfTimeHoursMet = true;
                        this.planGrouped = plan;
                        this.loaded = true;
                    })
                    .catch(() => {
                        this.$vs.notify({text: 'Nie udało się pobrać planu użytkownika.', color: 'danger'});
                    })
                    .then(() => {
                        this.visible = true;
                    });
            },
            createPlanSlot: function (plan, name) {
                plan[name] = new Array(7);
                for (let i = 0 ; i < plan[name].length ; i++)
                    plan[name][i] = [];
                return plan
            },
            planSort: (p1, p2) => {
                if (p1.from_hour !== p2.from_hour)
                    return p1.from_hour - p2.from_hour;
                if (p1.to_hour !== p2.to_hour)
                    return p1.to_hour - p2.to_hour;
                if (p1.week_id !== p2.week_id)
                    return p1.week_id - p2.week_id;
                return p1.place - p2.place;
            },
            planDuration: function (plan) {
                return plan.to_hour - plan.from_hour;
            },
            updateCreated: function (createdPlan) {
                this.planGrouped[this.loggedUsername][createdPlan.dow-1].push(createdPlan);
                this.planGrouped[this.loggedUsername][createdPlan.dow-1].sort(this.planSort);
            },
            updateDeleted: function (deletedPlan) {
                const planIndex = this.planGrouped[this.loggedUsername][deletedPlan.dow-1].findIndex(e => e.id === deletedPlan.id);
                this.planGrouped[this.loggedUsername][deletedPlan.dow-1].splice(planIndex, 1);
            },
            updateUpdated: function (updatedPlan) {
                const planIndex = this.planGrouped[this.loggedUsername][updatedPlan.dow-1].findIndex(e => e.id === updatedPlan.id);
                this.planGrouped[this.loggedUsername][updatedPlan.dow-1][planIndex] = updatedPlan;
                this.planGrouped[this.loggedUsername][updatedPlan.dow-1].sort(this.planSort);
            },
            weekHours: function (plan) {
                let res = {};
                 for (let dow of plan) {
                     for (let planElement of dow) {
                         let duration = this.planDuration(planElement);
                         if (res[planElement.place] === undefined) {
                              res[planElement.place] = {current:0, next:0};
                         }
                         if (this.matchingCurrentWeekId(planElement.week_id)) {
                             res[planElement.place].current += duration;
                         }
                         if (this.matchingNextWeekId(planElement.week_id)) {
                             res[planElement.place].next += duration;
                         }
                     }
                 }
                return res;
            },
            weekMinutesFormatted: function (minutes) {
                let hours = Math.floor(minutes / 60);
                let minutes_remainder = minutes % 60;
                if (minutes_remainder !== 0) {
                    return `${hours}:${minutes_remainder.toString().padStart(2, '0')}`;
                }
                return hours.toString();
            },
            weekHoursSum: function (plan) {
                return Object.values(this.weekHours(plan)).reduce((a, b)=>({current : a.current + b.current, next: a.next + b.next}), {current:0, next:0});
            },
            hoursSumWeekParity: function(plan) {
                let duration = this.weekHoursSum(plan);
                if (duration.current === duration.next)
                    return this.weekMinutesFormatted(duration.current);
                return this.weekMinutesFormatted(duration.current) + " | " + this.weekMinutesFormatted(duration.next);
            },
            halfTimeHoursMet: function (plan) {
                let duration = this.weekHoursSum(plan);
                let ans = duration.current >= 20 * 60 && duration.next >= 20 * 60;
                this.allHalfTimeHoursMet = this.allHalfTimeHoursMet && ans;
                return ans;
            },
        },
    }
</script>

<style lang="scss" scoped>
    .highlight-today {
        color: #ff6200;
    }

    .adjust-plan {
        font-size: 14px;
        text-decoration: underline;
        &:hover {
            color: #ff6200;
        }

    }

    .adjust-plan:hover {
        text-decoration: underline;
        cursor: pointer;
    }

    .top-header-row {
        > * {
            display: inline-block;
        }
        > div:first-child {
            margin-right: 1rem;
        }
          > div:nth-child(2) {
            margin-top: 1rem;
        }
    }

    .table-pp {
        td {
            padding-left: 0;
            padding-right: 0;
        }
        th {
            border-top:none;
        }
    }

    .color-legend {
        text-align: center;
        overflow: hidden;
        float: right;
        margin-top: 1rem;
        border: 1px solid #e7e7e7;
        border-radius: 2px;
        > div {
            float: right;
            padding: 2px 5px;
            font-size: .8rem;
        }
     }

    .right-border-column {
        text-align: center;
        border-right:  1px solid #e7e7e7;;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .3s;
    }

    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
    {
        opacity: 0;
    }

    .btn-link {
        line-height: 1.4rem;
        font-size: 1.4rem;
        &:hover {
            text-decoration: none;
            color: #ff6200;
        }
    }

    .week-hours-places {
        text-align: center;
        border: 1px solid #e7e7e7;
        border-radius: 2px;
        > div {
            padding: 2px 5px;
            font-size: .6rem;
        }
    }

    .half-time-hours-warning {
        color: #e7525d;
    }

</style>



// WEBPACK FOOTER //
// WorktimePlan.vue?073ba9cc